import {
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconArrowRight,
  IconClipboardCheck,
  IconSearch,
  IconUser,
} from "@/lib/icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { getWorklist } from "@/lib/api/examinations";
import { formatDate, formatDateTime } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

export function WorklistPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["worklist"],
    // include completed sections too so doctors can re-edit their own work
    queryFn: () => getWorklist(false),
    refetchInterval: 30_000,
  });

  // For lab tech the user has no `specialty` field, but their sections are "laboratory"
  const editorSpecialty =
    user?.specialty || (user?.role === "lab_tech" ? "laboratory" : "");

  const items = data?.items ?? [];
  const filtered = !debSearch.trim()
    ? items
    : items.filter((it) => {
        const q = debSearch.trim().toLowerCase();
        return (
          it.patient_name.toLowerCase().includes(q) ||
          it.patient_card_number.toLowerCase().includes(q) ||
          String(it.patient_age).includes(q)
        );
      });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("worklist.title")}
        subtitle={user?.specialty ? t(`users.specialties.${user.specialty}`) : undefined}
      />

      <Card padding="md">
        <TextInput
          rightSection={<IconSearch size={18} strokeWidth={2} />}
          placeholder={t("examinations.search_my_tasks")}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0}>
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("patients.fields.card")}</Table.Th>
                <Table.Th>{t("patients.fields.name")}</Table.Th>
                <Table.Th>{t("patients.fields.age")}</Table.Th>
                <Table.Th>{t("examinations.fields.type")}</Table.Th>
                <Table.Th>{t("examinations.fields.section_status")}</Table.Th>
                <Table.Th>{t("worklist.fields.waiting_since")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <EmptyState
                      icon={<IconClipboardCheck size={32} strokeWidth={1.5} />}
                      title={t("worklist.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.map((item) => (
                <Table.Tr key={item.section_id}>
                  <Table.Td>
                    <Text ff="monospace" fw={600} size="sm">
                      {item.patient_card_number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        size={32}
                        radius="md"
                        variant="gradient"
                        gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
                      >
                        <IconUser size={16} strokeWidth={1.6} />
                      </Avatar>
                      <Text fw={600} size="sm">
                        {item.patient_name}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {item.patient_age}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" radius="sm">
                      {t(`examinations.types.${item.examination_type}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={item.completed_at ? "brand" : "orange"}
                      radius="sm"
                    >
                      {item.completed_at
                        ? t("examinations.statuses.completed")
                        : t("examinations.statuses.pending")}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDateTime(item.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant={item.completed_at ? "light" : "filled"}
                      rightSection={<IconArrowRight size={14} strokeWidth={1.8} />}
                      disabled={!editorSpecialty}
                      onClick={() =>
                        navigate(
                          `/examinations/${item.examination_id}/sections/${editorSpecialty}`,
                        )
                      }
                    >
                      {item.completed_at ? t("common.edit") : t("worklist.open")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
